import { Shield, AlertTriangle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Visual "HIPAA Compliant" trust badge. Shown on public pages (landing,
 * patient auth screens, footer) as a reassurance signal that PHI is
 * handled under HIPAA. Clicks through to /hipaa-compliance for the
 * detailed disclosure. Unlike the session-lock banners below, this is a
 * lightweight pill/seal — no session state, no dismiss, safe to render
 * anywhere (server or client).
 *
 * Variants:
 *   - "pill" (default): compact rounded-full chip. Best inline near CTAs.
 *   - "seal":           larger vertical seal card, 2 lines. Best for
 *                       standalone reassurance blocks (auth screens).
 *   - "footer":         monochrome sm chip tuned for the site footer.
 */
export function HIPAABadge({ variant = "pill", className = "" }) {
  const testId = `hipaa-badge-${variant}`;
  if (variant === "seal") {
    return (
      <Link
        to="/hipaa-compliance"
        className={`inline-flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 hover:bg-emerald-50 px-4 py-3 transition-colors ${className}`}
        data-testid={testId}
        aria-label="HIPAA Compliant — learn more"
      >
        <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
          <ShieldCheck className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="text-left">
          <p className="text-sm font-bold text-emerald-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            HIPAA Compliant
          </p>
          <p className="text-[11px] text-emerald-700/80 leading-snug mt-0.5">
            Your health information is encrypted, access-logged, and never sold.
          </p>
        </div>
      </Link>
    );
  }
  if (variant === "footer") {
    return (
      <Link
        to="/hipaa-compliance"
        className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white/60 hover:bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 transition-colors ${className}`}
        data-testid={testId}
        aria-label="HIPAA Compliant"
        title="HIPAA Compliant — click for details"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.4} />
        HIPAA Compliant
      </Link>
    );
  }
  // Default: pill
  return (
    <Link
      to="/hipaa-compliance"
      className={`inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 text-xs font-bold shadow-sm transition-colors ${className}`}
      data-testid={testId}
      aria-label="HIPAA Compliant — learn more"
      title="HIPAA Compliant"
    >
      <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
      HIPAA Compliant
    </Link>
  );
}

export function HIPAABanner() {
  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 mb-4" data-testid="hipaa-banner">
      <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
      <span>HIPAA-Compliant Session &mdash; PHI access is logged. 15-min inactivity timeout active.</span>
    </div>
  );
}

export function HIPAAPatientBanner() {
  return (
    <div className="bg-purple-50 border border-purple-100 rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs text-purple-700 mb-4" data-testid="hipaa-patient-banner">
      <Shield className="w-4 h-4 text-purple-500 flex-shrink-0" />
      <span>Your health information is protected under HIPAA. Session auto-locks after 15 minutes of inactivity.</span>
    </div>
  );
}

export function SessionTimeoutAlert() {
  const timedOut = sessionStorage.getItem("glpflo_session_timeout") === "1";
  if (!timedOut) return null;

  const handleDismiss = () => {
    sessionStorage.removeItem("glpflo_session_timeout");
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="session-timeout-alert">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-5">
          <AlertTriangle className="w-7 h-7 text-amber-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Session Expired</h2>
        <p className="text-slate-500 text-sm mb-6">
          Your session was automatically ended after 15 minutes of inactivity to protect your health information (HIPAA compliance).
        </p>
        <button
          onClick={handleDismiss}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-3 text-sm font-medium transition-colors"
          data-testid="session-timeout-dismiss-btn"
        >
          Sign In Again
        </button>
      </div>
    </div>
  );
}
